import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/layout/DashboardLayout';
import { EmptyState } from '../components/shared/EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Search, Box, AlertTriangle, ArrowUpRight, ArrowDownRight, Pencil, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const MAJOR_CURRENCIES = [
  { code: 'USD', name: 'US Dollar (USD)' },
  { code: 'EUR', name: 'Euro (EUR)' },
  { code: 'GBP', name: 'British Pound (GBP)' },
  { code: 'JPY', name: 'Japanese Yen (JPY)' },
  { code: 'AUD', name: 'Australian Dollar (AUD)' },
  { code: 'CAD', name: 'Canadian Dollar (CAD)' },
  { code: 'CHF', name: 'Swiss Franc (CHF)' },
  { code: 'CNY', name: 'Chinese Yuan (CNY)' },
  { code: 'INR', name: 'Indian Rupee (INR)' }
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SparePartsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'superadmin' || user?.role === 'platform_admin';
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(user?.org_id || 'all');
  const [selectedAsset, setSelectedAsset] = useState('all');

  const [parts, setParts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTxDialog, setShowTxDialog] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [txType, setTxType] = useState('consume');
  
  const [isEditing, setIsEditing] = useState(false);
  const [editPartId, setEditPartId] = useState(null);
  
  // Form states
  const [newPart, setNewPart] = useState({
    part_number: '', name: '', description: '', category: 'consumables',
    current_stock: 0, minimum_stock: 10, unit: 'pcs', unit_cost: 0, currency: 'USD', asset_id: 'none', supplier: '', location: ''
  });
  
  const [txData, setTxData] = useState({
    quantity: 1, notes: '', work_order_id: ''
  });

  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API}/organizations`).then(res => setOrganizations(res.data)).catch(console.error);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchData();
  }, [selectedOrg, selectedAsset]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedOrg && selectedOrg !== 'all') params.append('org_id', selectedOrg);
      if (selectedAsset && selectedAsset !== 'all') params.append('asset_id', selectedAsset);

      const assetParams = new URLSearchParams();
      if (selectedOrg && selectedOrg !== 'all') assetParams.append('org_id', selectedOrg);

      const [partsRes, assetsRes] = await Promise.all([
        axios.get(`${API}/spare-parts?${params}`),
        axios.get(`${API}/assets?${assetParams}`)
      ]);
      setParts(partsRes.data.items);
      setAssets(assetsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchParts = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedOrg && selectedOrg !== 'all') params.append('org_id', selectedOrg);
      if (selectedAsset && selectedAsset !== 'all') params.append('asset_id', selectedAsset);
      const { data } = await axios.get(`${API}/spare-parts?${params}`);
      setParts(data.items);
    } catch (error) {
      toast.error('Failed to load spare parts');
    }
  };

  const handleCloseDialog = () => {
    setShowCreateDialog(false);
    setIsEditing(false);
    setEditPartId(null);
    setNewPart({
      part_number: '', name: '', description: '', category: 'consumables',
      current_stock: 0, minimum_stock: 10, unit: 'pcs', unit_cost: 0, currency: 'USD', asset_id: 'none', supplier: '', location: ''
    });
  };

  const openEdit = (part) => {
    setIsEditing(true);
    setEditPartId(part.id);
    setNewPart({
      part_number: part.part_number || '',
      name: part.name || '',
      description: part.description || '',
      category: part.category || 'consumables',
      current_stock: part.current_stock || 0,
      minimum_stock: part.minimum_stock || 10,
      unit: part.unit || 'pcs',
      unit_cost: part.unit_cost || 0,
      currency: part.currency || 'USD',
      asset_id: part.asset_id || 'none',
      supplier: part.supplier || '',
      location: part.location || ''
    });
    setShowCreateDialog(true);
  };

  const handleSavePart = async () => {
    try {
      if (!newPart.name || !newPart.part_number) {
        toast.error('Name and Part Number are required');
        return;
      }
      
      const payload = {
        ...newPart,
        asset_id: newPart.asset_id === 'none' ? null : newPart.asset_id
      };

      if (isEditing) {
        await axios.patch(`${API}/spare-parts/${editPartId}`, payload);
        toast.success('Spare part updated successfully');
      } else {
        await axios.post(`${API}/spare-parts`, payload);
        toast.success('Spare part created successfully');
      }
      
      handleCloseDialog();
      fetchParts();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditing ? 'update' : 'create'} part`);
    }
  };

  const handleTransaction = async () => {
    try {
      if (txData.quantity <= 0) {
        toast.error('Quantity must be greater than 0');
        return;
      }
      await axios.post(`${API}/spare-parts/${selectedPart.id}/transactions`, {
        transaction_type: txType,
        ...txData
      });
      toast.success(`Inventory ${txType} recorded successfully`);
      setShowTxDialog(false);
      setTxData({ quantity: 1, notes: '', work_order_id: '' });
      fetchParts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transaction failed');
    }
  };

  const openTx = (part, type) => {
    setSelectedPart(part);
    setTxType(type);
    setTxData({ quantity: 1, notes: '', work_order_id: '' });
    setShowTxDialog(true);
  };

  const handleDeletePart = async (partId) => {
    if (!window.confirm("Are you sure you want to delete this spare part?")) return;
    try {
      await axios.delete(`${API}/spare-parts/${partId}`);
      toast.success("Spare part deleted successfully");
      fetchParts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete part");
    }
  };

  const filteredParts = parts.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    p.part_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Spare Parts Inventory</h1>
            <p className="text-slate-500">Manage stock levels, restocks, and consumption</p>
          </div>
          <Button onClick={() => { handleCloseDialog(); setShowCreateDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Add New Part
          </Button>
        </div>

        {/* Toolbar */}
        <Card>
          <CardContent className="p-4 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search parts via name or number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              {isAdmin && (
                <Select value={selectedOrg} onValueChange={(val) => { setSelectedOrg(val); setSelectedAsset('all'); }}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="All Organizations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Organizations</SelectItem>
                    {organizations.map(org => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={selectedAsset} onValueChange={setSelectedAsset}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Assets" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Assets</SelectItem>
                  {assets.map(asset => (
                    <SelectItem key={asset.id} value={asset.id}>{asset.name} ({asset.asset_id})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Parts Grid */}
        {loading ? (
          <div className="flex justify-center p-12">
            <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
          </div>
        ) : filteredParts.length === 0 ? (
          <EmptyState
            icon={Box}
            title="No spare parts found"
            description="Get started by adding your first spare part to the inventory."
            actionLabel="Add Spare Part"
            onAction={() => setShowCreateDialog(true)}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredParts.map(part => {
              const isLowStock = part.current_stock <= part.minimum_stock;
              const isOut = part.current_stock === 0;

              return (
                <Card key={part.id} className={isLowStock ? 'border-amber-200' : ''}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-lg text-slate-900">{part.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-sm text-slate-500 font-mono">{part.part_number}</p>
                          {part.asset_id && (
                            <Badge variant="secondary" className="font-normal text-xs text-slate-600">
                              {assets.find(a => a.id === part.asset_id)?.name || 'Linked Asset'}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {isOut ? (
                          <Badge variant="destructive">Out of Stock</Badge>
                        ) : isLowStock ? (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Low Stock
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            In Stock
                          </Badge>
                        )}
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-6 px-2 text-slate-400 hover:text-slate-600" onClick={() => openEdit(part)}>
                            <Pencil className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          <Button variant="ghost" size="sm" title="Delete" className="h-6 px-2 text-slate-400 hover:text-red-600" onClick={() => handleDeletePart(part.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="p-2 bg-slate-50 rounded-md">
                        <p className="text-slate-500">Current Stock</p>
                        <p className={`font-semibold text-lg ${isLowStock ? 'text-amber-600' : 'text-slate-900'}`}>
                          {part.current_stock} <span className="text-sm font-normal text-slate-500">{part.unit || 'pcs'}</span>
                        </p>
                      </div>
                      <div className="p-2 bg-slate-50 rounded-md">
                        <p className="text-slate-500">Min. Target</p>
                        <p className="font-semibold text-lg text-slate-700">
                          {part.minimum_stock} <span className="text-sm font-normal text-slate-500">{part.unit || 'pcs'}</span>
                        </p>
                      </div>
                    </div>
                    {part.location && (
                      <p className="text-sm text-slate-600">
                        <span className="font-medium">Location: </span>{part.location}
                      </p>
                    )}
                    
                    <div className="flex gap-2 pt-2 border-t">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full"
                        onClick={() => openTx(part, 'restock')}
                      >
                        <ArrowDownRight className="w-4 h-4 mr-2" />
                        Restock
                      </Button>
                      <Button 
                        variant="default" 
                        size="sm" 
                        className="w-full"
                        onClick={() => openTx(part, 'consume')}
                        disabled={isOut}
                      >
                        <ArrowUpRight className="w-4 h-4 mr-2" />
                        Consume
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create / Edit Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={(open) => !open && handleCloseDialog()}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{isEditing ? 'Edit Spare Part' : 'Add Spare Part'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Part Number *</Label>
                  <Input 
                    value={newPart.part_number} 
                    onChange={e => setNewPart({...newPart, part_number: e.target.value})}
                    placeholder="PN-1234"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input 
                    value={newPart.name} 
                    onChange={e => setNewPart({...newPart, name: e.target.value})}
                    placeholder="Inverter Filter"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input 
                  value={newPart.description} 
                  onChange={e => setNewPart({...newPart, description: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Linked Asset</Label>
                <Select value={newPart.asset_id} onValueChange={val => setNewPart({...newPart, asset_id: val})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Asset" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {assets.map(asset => (
                      <SelectItem key={asset.id} value={asset.id}>
                        {asset.name} ({asset.asset_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Current Stock</Label>
                  <div className="flex gap-2">
                    <Input 
                      type="number"
                      className="flex-1"
                      value={newPart.current_stock} 
                      onChange={e => setNewPart({...newPart, current_stock: parseInt(e.target.value) || 0})}
                    />
                    <Input 
                      className="w-24 text-center"
                      placeholder="Unit"
                      value={newPart.unit} 
                      onChange={e => setNewPart({...newPart, unit: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Minimum Target ({newPart.unit || 'pcs'})</Label>
                  <Input 
                    type="number"
                    value={newPart.minimum_stock} 
                    onChange={e => setNewPart({...newPart, minimum_stock: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Location / Bin</Label>
                  <Input 
                    value={newPart.location} 
                    onChange={e => setNewPart({...newPart, location: e.target.value})}
                    placeholder="Aisle 4, Bin B"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cost per unit</Label>
                  <div className="flex gap-2">
                    <Select value={newPart.currency} onValueChange={val => setNewPart({...newPart, currency: val})}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MAJOR_CURRENCIES.map(curr => (
                          <SelectItem key={curr.code} value={curr.code}>
                            {curr.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input 
                      type="number"
                      step="0.01"
                      className="flex-1"
                      value={newPart.unit_cost} 
                      onChange={e => setNewPart({...newPart, unit_cost: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseDialog}>Cancel</Button>
              <Button onClick={handleSavePart} disabled={!newPart.name || !newPart.part_number}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Transaction Dialog */}
        <Dialog open={showTxDialog} onOpenChange={setShowTxDialog}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>
                {txType === 'consume' ? 'Consume Part' : 'Restock Part'}
              </DialogTitle>
            </DialogHeader>
             {selectedPart && (
               <div className="space-y-4 pt-4">
                <div className="p-3 bg-slate-50 rounded-md mb-4 border text-sm">
                  <p><span className="font-semibold">{selectedPart.name}</span> ({selectedPart.part_number})</p>
                  <p className="text-slate-500 mt-1">Current Stock: {selectedPart.current_stock}</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input 
                    type="number"
                    min="1"
                    max={txType === 'consume' ? selectedPart.current_stock : 9999}
                    value={txData.quantity} 
                    onChange={e => setTxData({...txData, quantity: parseInt(e.target.value) || 0})}
                  />
                </div>
                
                {txType === 'consume' && (
                  <div className="space-y-2">
                    <Label>Linked Work Order (optional)</Label>
                    <Input 
                      value={txData.work_order_id} 
                      onChange={e => setTxData({...txData, work_order_id: e.target.value})}
                      placeholder="WO-2026-..."
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input 
                    value={txData.notes} 
                    onChange={e => setTxData({...txData, notes: e.target.value})}
                    placeholder={txType === 'consume' ? "Used for repair..." : "Received shipment..."}
                  />
                </div>
              </div>
             )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTxDialog(false)}>Cancel</Button>
              <Button onClick={handleTransaction}>Confirm {txType === 'consume' ? 'Consumption' : 'Restock'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
}
